import React, { useState } from 'react';
import GeocodingService from '../../services/geocoding';
import { Search, MapPin, Edit3, X, CheckCircle } from 'lucide-react';

/**
 * Enhanced Postal Code Lookup Component with Display & Edit
 * 
 * Usage:
 * <PostalCodeLookup
 *   value={formData.postal_code} // Current stored postal code
 *   onAddressFound={({ address, latitude, longitude, postal_code }) => {
 *     setFormData({
 *       ...formData,
 *       address: address,
 *       postal_code: postal_code,
 *       latitude: latitude,
 *       longitude: longitude
 *     });
 *   }}
 *   onPostalCodeChange={(newPostalCode) => {
 *     setFormData({ ...formData, postal_code: newPostalCode });
 *   }}
 *   placeholder="Enter postal code (e.g., 018989)"
 *   disabled={false}
 * />
 */
const PostalCodeLookup = ({
  value = '', // Current stored postal code
  onAddressFound,
  onPostalCodeChange, // Callback for postal code changes
  onError,
  placeholder = "Enter postal code (e.g., 018989)",
  disabled = false,
  className = "",
  buttonText = "Look Up",
  country = "SG",
  showStoredValue = true, // Whether to show the stored postal code
  isAdmin = false // Theme support for admin vs employee pages
}) => {
  const [postalCode, setPostalCode] = useState(value || ''); // Initialize with current value
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Update postal code when value prop changes
  React.useEffect(() => {
    setPostalCode(value || '');
  }, [value]);

  const handleLookup = async () => {
    if (!postalCode.trim()) {
      return;
    }

    // Validate postal code format before API call
    if (country === 'SG' && !GeocodingService.isValidSingaporePostalCode(postalCode)) {
      if (onError) {
        onError('Please enter a valid 6-digit Singapore postal code');
      }
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await GeocodingService.geocodeWithFeedback(
        postalCode,
        (data) => {
          setLastResult(data);
          if (onAddressFound) {
            onAddressFound(data);
          }
        },
        (error) => {
          setLastResult(null);
          if (onError) {
            onError(error);
          }
        },
        country
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setPostalCode(value);
    
    // Update the stored postal code if callback provided
    if (onPostalCodeChange) {
      onPostalCodeChange(value);
    }
    
    // Clear previous result when typing
    if (lastResult) {
      setLastResult(null);
    }
  };

  return (
    <div className={`postal-code-lookup card ${className}`}>
      <div className="card-content">
        {/* Postal Code Lookup Section */}
        <div className="form-group">
          <label className="form-label">
            <Search className="w-4 h-4 inline mr-2" />
            Postal Code Lookup
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="input-icon" />
              <input
                type="text"
                value={postalCode}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                disabled={disabled || isLoading}
                maxLength={country === 'SG' ? 7 : 10}
                className={`${isAdmin ? 'input-admin' : 'input'} input-with-icon`}
              />
            </div>

            {/* Lookup Button - Icon Only */}
            <button
              onClick={handleLookup}
              disabled={disabled || isLoading || !postalCode.trim()}
              className={`btn ${isAdmin ? 'btn-admin-primary' : 'btn-primary'} w-12 h-12 p-0 flex`}
              aria-label={buttonText || "Find Address"}
              title={buttonText || "Find Address"}
            >
              {isLoading ? (
                <div className={`w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin ${isAdmin ? '' : ''}`} />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {/* Format Helper */}
          {country === 'SG' && (
            <p className="form-help">
              Singapore format: 6 digits (e.g., 018989)
            </p>
          )}
        </div>

        {/* Results Display */}
        {lastResult && (
          <div className="mt-4 alert alert-success">
            <CheckCircle className="alert-icon" />
            <div>
              <p className="text-sm font-medium">
                Address found: {lastResult.address}
              </p>
              <p className="text-xs mt-1">
                📌 Coordinates: {lastResult.latitude}, {lastResult.longitude}
              </p>
              <p className="text-xs mt-2">
                💡 <strong>Note:</strong> Don't forget to add the unit number (e.g., #12-34) if applicable
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostalCodeLookup; 